import {lazy, Suspense, useEffect, useId, useState} from 'react'
import {twJoin} from 'tailwind-merge'

import {LazyImportPlayer, Loading, Media, usePlayersStore} from '@sdox/ui'
import {useCurrentContactPerson, useCurrentOffice} from '@sdox/api'

import {Avatar} from './Avatar'
import {MediaOverlayAvatar} from './MediaOverlayAvatar'

import type {MediaPlayerInstance} from '@vidstack/react'

const Player = lazy(() => LazyImportPlayer)

export const ContactHeader = () => {
  const office = useCurrentOffice()
  const contactPerson = useCurrentContactPerson()
  const playerId = useId()
  const [playing, setPlaying] = useState(false)
  const player: MediaPlayerInstance | undefined = usePlayersStore(store => store.players[playerId])

  const hasVideo = !!contactPerson?.video
  const showThumbnail = !hasVideo && !!office?.thumbnailUrl
  const hasMedia = hasVideo || showThumbnail

  useEffect(() => {
    if (player === undefined) return
    const disposePlay = player.listen('play', () => setPlaying(true))
    const disposePause = player.listen('pause', () => setPlaying(false))

    return () => {
      disposePlay()
      disposePause()
      setPlaying(false)
    }
  }, [player])

  return (
    <div
      className={twJoin(
        'flex flex-col items-center',
        !hasMedia && 'pt-spacing-32 px-spacing-32',
      )}
    >
      {showThumbnail ? (
        <Media
          src={office.thumbnailUrl}
          className={'z-10 -mb-spacing-48 w-full'}
          aspectRatio={'16/9'}
        />
      ) : null}
      {contactPerson?.video ? (
        <Suspense fallback={<Loading/>}>
          <Player
            id={playerId}
            url={contactPerson?.video.src}
            stopOnLeave
            thumbnail={contactPerson?.video?.thumbnail ?? undefined}
            tracking={{prefix: 'ContactPersonDialog', piece: 'VideoStartClick'}}
            className={'z-10 -mb-spacing-48'}
          />
        </Suspense>
      ) : null}
      <div
        className={'relative flex flex-col items-center'}
      >
        {<Avatar thumbnail={contactPerson?.thumbnail} />}
        {hasMedia ? (
          <MediaOverlayAvatar
            thumbnail={contactPerson?.thumbnail}
            playing={playing}
          />
        ) : null}
        <span className={'text-h4 text-glyph-default-primary'}>
          {contactPerson?.fullName}
        </span>
        {contactPerson?.title ? (
          <span className={'text-p1 text-glyph-default-secondary'}>
            {contactPerson?.title}
          </span>
        ) : null}
      </div>
    </div>
  )
}
